// What the topbar says you are looking at. One entry per route.
//
// The title lives here rather than in the page because the topbar renders it,
// and the topbar sits in the layout — above every page, mounted once. Pages own
// their content; the chrome names them.
//
// These are NOT the sidebar labels. lib/nav-config.ts answers "what do I click
// to get there", and its labels are short. This answers "where am I", and the
// two are allowed to differ: you click "Training Guide" and arrive at the "YWS
// Creator Brief". Each title below is the page's own <h1>, moved, not reworded.

import { skillName, skillPath } from "@/skills/skills";
import { GRADED_RUNS_HREF, REPORT_REVIEW_NAME, REPORT_REVIEW_SKILL_PATH, RETIRED_SKILLS_HREF, SKILLS_PATH } from "@/lib/skill-pages";

export interface Crumb {
  href: string;
  label: string;
}

export interface PageHeader {
  /**
   * `null` means the route has no title of its own — the page supplies one at
   * runtime via <PageMeta>. The editor is the one case: "Script Editor" said
   * nothing "Jay Crotty · Week 5" didn't already say better, so there is no
   * static title to fall back to, only the dynamic one.
   */
  title: string | null;
  /** Ancestors, nearest last. The title itself is never a crumb. */
  crumbs: Crumb[];
}

const SCRIPTS_CRUMB: Crumb = { href: "/scripts/videos", label: "Scripts" };
// Script Grading's Graded Runs tab, the list a run is opened from, in the tab's own words.
const GRADED_RUNS_CRUMB: Crumb = { href: GRADED_RUNS_HREF, label: "Graded Runs" };
// ⚠️ "Client Onboarding", IN THE SIDEBAR'S OWN WORDS. This one crumb does NOT
// take the licence the note above grants — it says exactly what the nav entry
// it points at says, because what you are going back to IS that entry's page,
// and hearing it named the same way twice is what makes the way back obvious.
//
// It said "Clients" until 2026-09-07, on the reasoning that a crumb names the
// LIST you came from while the nav names the section. That reasoning was right
// about the label it was avoiding and wrong about this one: "Onboarding ›
// Nomadix" does read as a section with a client inside it, but "Client
// Onboarding › Nomadix" reads as the work and whose it is.
const FIRST30_CLIENTS_CRUMB: Crumb = { href: "/onboarding/first30", label: "Client Onboarding" };
// The same rule as the line above: the crumb says what the nav entry it points
// at says, because what you are going back to IS that entry's page.
const CASTINGS_CRUMB: Crumb = { href: "/sourcing", label: "Creator Selection" };
// The same rule again: back to the work table, in the nav entry's own words.
const AFFILIATES_CRUMB: Crumb = { href: "/brands/affiliates", label: "Affiliate Campaigns" };
// A retired skill's way back: the Skills page's Retired tab, which lists it.
const RETIRED_SKILLS_CRUMB: Crumb = { href: RETIRED_SKILLS_HREF, label: "Retired skills" };

const PAGE_HEADERS: Record<string, PageHeader> = {
  // Every skill, Active and Retired: a nav entry of its own (Admin), so no crumb.
  [SKILLS_PATH]: { title: "Skills", crumbs: [] },
  "/settings/users": { title: "Users", crumbs: [] },
  // "Brands & access" in the sidebar, where it has to stay short. This line has
  // the room to say what the page is FOR, which is one question asked about two
  // kinds of identity: our brands, and the vendor accounts and grants that
  // measure them.
  "/settings/brands": { title: "Brands, and who still has access", crumbs: [] },
  // "Onboarding Template" in the sidebar; this line has the room to say what it
  // is a template OF, and that editing it is about the next client rather than
  // the ones on /onboarding/first30.
  "/settings/onboarding-template": { title: "Onboarding Checklist Template", crumbs: [] },
  "/settings/integrations": { title: "Settings", crumbs: [] },
  "/feedback": { title: "Feedback", crumbs: [] },
  "/scripts/videos": { title: "Scripts", crumbs: [] },
  // The page carries its own SCOPE path — Agency › Pod › Brand › Creator — and
  // that is navigation within one page rather than a route ancestry, so it is
  // not a crumb here. The chrome says which page you are on; the page says which
  // slice of it.
  "/throughput": { title: "Throughput", crumbs: [] },
  // "Brand Health" in the sidebar, under a heading of its own, where it has to
  // stay short. This line has the room to say what the page is: not the brand
  // ADMIN (that is /settings/brands) but the relationship, brand by brand.
  //
  // ⚠️ THE COMMENT HERE USED TO SAY "Brands in the sidebar, where it sits beside
  // Throughput", and both halves stopped being true on 2026-09-11 — the entry
  // was renamed and moved out of Creator Scripts into its own section. The title
  // itself is unchanged and deliberately so: short in the nav, descriptive in
  // the header is the same pattern /settings/brands and the onboarding template
  // follow two lines up. A QA pass caught the stale reasoning, not a stale name.
  "/brands": { title: "The book, and what each brand is doing", crumbs: [] },
  // The sidebar's words plus the span they cover: the nav entry has to stay
  // short beside "Throughput" and "Schedule", and this line has the room to say
  // which thirty days it means.
  //
  // ⚠️ CAPITALISED WHERE THE LINES AROUND IT ARE SENTENCE CASE, and deliberately.
  // "Client Onboarding" here is the SECTION'S NAME as the sidebar says it, not a
  // description of a page — so it is spelled the way the thing it names is
  // spelled, and only the trailing span stays lowercase.
  "/onboarding/first30": { title: "Client Onboarding — First 30 days", crumbs: [] },
  // The sidebar's words exactly, as the two entries around it are. This line
  // used to spell out what is being measured — "Onboarding — how long each step
  // sits waiting" — which was the page's caption row said twice, once here and
  // once on the page. The caption row is gone and the h2 under it still says
  // "Actionable time by step" and what that means.
  "/onboarding/first30/insights": { title: "Onboarding Insights", crumbs: [] },
  // The sidebar's words exactly, as Client Onboarding's title is — this entry
  // and its page settled on one name rather than the nav being short and the
  // title being descriptive.
  //
  // ⚠️ CAPITALISED WHERE THE LINES AROUND IT ARE SENTENCE CASE, for the reason
  // given on /onboarding/first30 above: it is the SECTION'S NAME as the sidebar
  // says it, not a description of a page.
  "/sourcing": { title: "Creator Selection", crumbs: [] },
  // ⚠️ IT HAS TO BE HERE AND NOT ONLY ON THE NAV. `headerFor` falls through to
  // "anything under /sourcing/ is one casting", which would have given this
  // page the casting header — no title of its own and a crumb back to the
  // castings list, as though a reader had drilled into it. It is a sibling
  // entry with its own nav slot, so it gets its own line, exactly as
  // /onboarding/first30/insights does one feature over.
  "/sourcing/insights": { title: "Selection Insights", crumbs: [] },
  // The sidebar's words exactly, as the two Insights entries are. The page's own
  // h2 says what is being counted ("Hours worked per week"), so a longer title
  // here would be that sentence twice, an inch apart.
  "/client-hours": { title: "Client Hours", crumbs: [] },
  "/scripts/guide": { title: "YWS Creator Brief — Script & Video", crumbs: [] },
  "/scripts/faq": { title: "FAQ / Lessons", crumbs: [] },
  [skillPath("grading")]: { title: skillName("grading"), crumbs: [] },
  [skillPath("script_import")]: { title: skillName("script_import"), crumbs: [SCRIPTS_CRUMB] },
  // A skill page's title is its name, the registry's (skills/skills.ts
  // `skillName`), so the title, the nav and LLM costs
  // say one thing; tests/unit/skill-names.test.ts holds every skill page to it.
  // That ended "How Scripts Get Graded" and "How Weekly Reports Get Written",
  // and Report Review, the Weekly Report's check, which calls no model,
  // took its nav item's words to match its sibling.
  [skillPath("weekly_report_draft")]: { title: skillName("weekly_report_draft"), crumbs: [] },
  [REPORT_REVIEW_SKILL_PATH]: { title: REPORT_REVIEW_NAME, crumbs: [] },
  // Affiliate campaigns, in the prototype's own headings —
  // its h1s, moved here as every title above was. The two lists are nav entries
  // and take no crumb; the skill and the write log take the prototype's.
  "/brands/affiliates": { title: "Affiliate campaigns", crumbs: [] },
  "/brands/affiliate-metrics": { title: "Affiliate metrics", crumbs: [] },
  // Client Slack: the mock's own title.
  "/brands/response-times": { title: "Response Times", crumbs: [] },
  "/brands/inbox": { title: "Client Inbox", crumbs: [] },
  "/brands/action-items": { title: "Action Items", crumbs: [] },
  // The prototype's own title.
  "/inbox": { title: "Operations Inbox", crumbs: [] },
  [skillPath("dm_replies")]: { title: skillName("dm_replies"), crumbs: [AFFILIATES_CRUMB] },
  // The skills that had no page, each with the way back
  // to the page that runs it: the affiliate checks to the work table, as DM
  // Replies, and Creator Selection's two to the castings. Each skill page is
  // /skills/<slug>, read from the registry.
  [skillPath("creator_video")]: { title: skillName("creator_video"), crumbs: [AFFILIATES_CRUMB] },
  [skillPath("creator_ai_content")]: { title: skillName("creator_ai_content"), crumbs: [AFFILIATES_CRUMB] },
  [skillPath("creator_persona_fit")]: { title: skillName("creator_persona_fit"), crumbs: [AFFILIATES_CRUMB] },
  [skillPath("brief_parser")]: { title: skillName("brief_parser"), crumbs: [CASTINGS_CRUMB] },
  [skillPath("candidate_review")]: { title: skillName("candidate_review"), crumbs: [CASTINGS_CRUMB] },
  // Client Onboarding's two with the way back to its list, which the
  // intake and each client's record and deck are reached from. The meeting
  // review has no crumb: the page it serves, Your Fathom, is the AM's and the
  // admin's, and AM Ops would be handed a link it cannot open.
  [skillPath("first30_profile")]: { title: skillName("first30_profile"), crumbs: [FIRST30_CLIENTS_CRUMB] },
  [skillPath("ftc_request")]: { title: skillName("ftc_request"), crumbs: [FIRST30_CLIENTS_CRUMB] },
  [skillPath("deck_draft")]: { title: skillName("deck_draft"), crumbs: [FIRST30_CLIENTS_CRUMB] },
  [skillPath("fathom_meeting")]: { title: skillName("fathom_meeting"), crumbs: [] },
  // Client Slack's two skills, each with the way back to the page it reads for.
  // Retired: reached from the Skills page's Retired tab, so the way back is that list.
  [skillPath("client_slack_triage")]: { title: skillName("client_slack_triage"), crumbs: [RETIRED_SKILLS_CRUMB] },
  // The set-aside check (retired 2026-10-23) had no line and drew no title until the names' test asked.
  [skillPath("client_slack_setaside")]: { title: skillName("client_slack_setaside"), crumbs: [RETIRED_SKILLS_CRUMB] },
  [skillPath("client_slack_action_items")]: { title: skillName("client_slack_action_items"), crumbs: [{ href: "/brands/action-items", label: "Action Items" }] },
  // The segmenter reads for the inbox as the triage does.
  [skillPath("client_slack_segment")]: { title: skillName("client_slack_segment"), crumbs: [{ href: "/brands/inbox", label: "Client Inbox" }] },
  // The answered skill decides what the inbox and Response Times count.
  [skillPath("client_slack_answered")]: { title: skillName("client_slack_answered"), crumbs: [{ href: "/brands/inbox", label: "Client Inbox" }] },
  // The respond skill writes what the inbox says about each owed message.
  [skillPath("client_slack_respond")]: { title: skillName("client_slack_respond"), crumbs: [{ href: "/brands/inbox", label: "Client Inbox" }] },
  // The Work Type skill names the Action Items page's Work type column.
  [skillPath("client_slack_item_kind")]: { title: skillName("client_slack_item_kind"), crumbs: [{ href: "/brands/action-items", label: "Action Items" }] },
  // The Missed Items check adds the items the Action Items read missed.
  // The re-check of quiet items.
  [skillPath("client_slack_recheck")]: { title: skillName("client_slack_recheck"), crumbs: [{ href: "/brands/action-items", label: "Action Items" }] },
  [skillPath("client_slack_items_check")]: { title: skillName("client_slack_items_check"), crumbs: [{ href: "/brands/action-items", label: "Action Items" }] },
  "/settings/integrations/reacher-writes": {
    title: "Reacher writes",
    crumbs: [{ href: "/settings/integrations", label: "Settings" }],
  },
  // Testing Connect Slack: reached from its card on Settings, so the crumb goes
  // back there, as Reacher writes' does.
  "/settings/integrations/client-slack": {
    title: "Client Slack",
    crumbs: [{ href: "/settings/integrations", label: "Settings" }],
  },
  // The TikTok browser: reached from its card on Settings, as Client Slack is.
  "/settings/integrations/tiktok": {
    title: "TikTok",
    crumbs: [{ href: "/settings/integrations", label: "Settings" }],
  },
  // A nav entry of its own (Admin), so no crumb.
  "/settings/llm-costs": { title: "LLM costs", crumbs: [] },
  // A nav entry of its own (Admin), so no crumb.
  "/settings/flags": { title: "Feature flags", crumbs: [] },
  // A nav entry of its own (Admin), so no crumb.
  "/settings/ops-keys": { title: "Ops keys", crumbs: [] },
  // Reached from the inbox's settings icon, so the crumb is the way back to it.
  "/settings/operations-inbox": { title: "Operations Inbox settings", crumbs: [{ href: "/inbox", label: "Operations Inbox" }] },
  "/settings/you": { title: "Your settings", crumbs: [] },
  // Connect Slack's home: a nav entry of its own (Admin), so no crumb.
  "/settings/slack": { title: "Your Slack", crumbs: [] },

  // The only route reached by drilling in rather than by clicking the nav, and
  // so the only one with a crumb: it is a week of one creator's videos, opened
  // from the Scripts table, and the way back is that table. Title is null: the
  // page names itself ("Jay Crotty · Week 5"), via <PageMeta>.
  "/scripts/editor": { title: null, crumbs: [SCRIPTS_CRUMB] },

  // The three Monitoring pages: each a nav entry of its own, so no crumb. The
  // Resources container view's way back is the page's own crumb, beside its
  // picker.
  "/monitoring/jobs": { title: "Jobs", crumbs: [] },
  "/monitoring/resources": { title: "Resources", crumbs: [] },
  "/monitoring/health": { title: "App health", crumbs: [] },
};

/**
 * One job's view: /monitoring/jobs/{job}. Reached from its row on the Jobs page,
 * which the crumb goes back to; titled by the page with the job's name
 * (<PageMeta>), and one of the affiliate tick's jobs adds the tick as a crumb.
 */
const JOB_HEADER: PageHeader = { title: null, crumbs: [{ href: "/monitoring/jobs", label: "Jobs" }] };

/**
 * One grading run: /admin/grades/{scriptId}/{runId}.
 *
 * Not in the map above because that is an exact-match lookup and this path is
 * dynamic. Same shape as the editor — the way back is the list you drilled in
 * from, and the title is null because the page names itself with the script it
 * is showing, via <PageMeta>. Which is also why the page has no back link of its
 * own: the crumb IS the way back, and duplicating it beside the script's title
 * put navigation and content on the same line.
 */
const RUN_HEADER: PageHeader = { title: null, crumbs: [GRADED_RUNS_CRUMB] };

/**
 * ONE CLIENT'S CHECKLIST: /onboarding/first30/c/{brand-slug}.
 *
 * Dynamic, so it is resolved below rather than sitting in the exact-match table.
 * Same shape as the run pages and the editor, and for the same reason: you
 * arrive by clicking a client rather than by clicking the nav, the crumb IS the
 * way back, and the title is null because the page names itself with the client
 * it is showing (<PageMeta> in components/first30/client-view.tsx).
 *
 * ⚠️ WHICH IS ALSO WHY THE CHECKLIST HAS NO BACK BUTTON OF ITS OWN. It had one,
 * and putting a crumb above it would be two ways back stacked vertically — the
 * duplication /admin/grades already refused.
 */
const FIRST30_CLIENT_HEADER: PageHeader = { title: null, crumbs: [FIRST30_CLIENTS_CRUMB] };

/**
 * ONE CASTING: /sourcing/{casting-id}.
 *
 * The fourth drilled-into route, and it takes the same shape as the other
 * three. Title null because the page names itself with the brand the casting is
 * for, and the crumb IS the way back — which is why the page carries no back
 * link of its own. It had one for about ten minutes; two ways back stacked
 * vertically is the duplication /admin/grades and the First 30 checklist both
 * refused before it.
 */
const CASTING_HEADER: PageHeader = { title: null, crumbs: [CASTINGS_CRUMB] };

/**
 * ONE CLIENT'S ACCOUNT RECORD: /onboarding/record/{brand-slug}, and the deck
 * beneath it.
 *
 * The fifth drilled-into route, and it takes the checklist's shape exactly —
 * the same crumb, back to the same list, because a record is reached by
 * clicking a client on /onboarding/first30 and that list IS the way back. Title
 * null because the page names itself with the client it is showing, via
 * <PageMeta>, so the record must carry no back link of its own either.
 *
 * ⚠️ IT IS HERE ONCE, FOR THE RECORD AND THE DECK BOTH, so that the two pages
 * do not each add it and disagree about the wording of the crumb. A page under
 * /onboarding/record/ that wants a title of its own takes an exact-match line
 * in the table above, the way /sourcing/insights does.
 */
const RECORD_HEADER: PageHeader = { title: null, crumbs: [FIRST30_CLIENTS_CRUMB] };

/**
 * ONE BRAND'S AFFILIATE PAGE: /brands/affiliates/{brand-slug}, and its New
 * affiliate campaign beneath it.
 *
 * The record's shape: reached by clicking a brand on the work table, which the
 * crumb goes back to, and titled by the page with the brand it shows (<PageMeta>).
 * The prototype's second crumb, the brand's pod, is not here: a crumb here is a
 * constant, and a pod is not. The page hands it to the topbar through <PageMeta
 * crumbs>, which draws it after this one, and /new adds the brand after the pod.
 */
const AFFILIATE_BRAND_HEADER: PageHeader = { title: null, crumbs: [AFFILIATES_CRUMB] };

/**
 * Null for a route with no chrome of its own — /signin and the access-pending
 * screen render outside the layout that holds the topbar, and "/" only ever
 * exists long enough to redirect (see homeRouteFor).
 */
export function headerFor(pathname: string): PageHeader | null {
  const exact = PAGE_HEADERS[pathname];
  if (exact) return exact;
  if (pathname.startsWith("/admin/grades/")) return RUN_HEADER;
  if (pathname.startsWith("/monitoring/jobs/")) return JOB_HEADER;
  // Insights is NOT here: it is its own nav entry with its own line in the
  // exact-match table above.
  if (pathname.startsWith("/onboarding/first30/c/")) return FIRST30_CLIENT_HEADER;
  // ⚠️ THE SLASH MATTERS HERE TOO, for the reason spelled out under /sourcing
  // below: a prefix without it would hand "/onboarding/record-export" the
  // record's chrome from a name collision. There is no /onboarding/record list
  // page — a record is always one client's — so nothing above it needs an
  // exact-match line.
  if (pathname.startsWith("/onboarding/record/")) return RECORD_HEADER;
  // ⚠️ THE SLASH MATTERS. "/sourcing" itself is the castings list and has its
  // own line in the exact-match table above; only something BENEATH it is one
  // casting. A prefix without the slash would strip the list's own title.
  if (pathname.startsWith("/sourcing/")) return CASTING_HEADER;
  // The slash keeps the work table's own title, as it does for /sourcing.
  if (pathname.startsWith("/brands/affiliates/")) return AFFILIATE_BRAND_HEADER;
  // A wait's popup is drawn over the inbox, so the inbox's title stays.
  if (pathname.startsWith("/brands/inbox/")) return PAGE_HEADERS["/brands/inbox"];
  // An item's popup and New action item the same, over Action Items.
  if (pathname.startsWith("/brands/action-items/")) return PAGE_HEADERS["/brands/action-items"];
  return null;
}
